import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronRight } from "lucide-react";
import api, { inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  Placed: "bg-secondary text-secondary-foreground",
  Confirmed: "bg-accent/20 text-accent-foreground",
  Shipped: "bg-primary/15 text-primary",
  "Out for Delivery": "bg-primary/15 text-primary",
  Delivered: "bg-primary text-primary-foreground",
  Cancelled: "bg-destructive/15 text-destructive",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/account/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12" data-testid="account-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">My Account</p>
          <h1 className="mt-3 text-4xl text-primary font-medium">Hello, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{user?.email}</p>
        </div>
        <Link to="/track" className="hidden sm:block text-sm text-primary link-underline">Track an order</Link>
      </div>

      <h2 className="font-display text-2xl text-primary mb-5">Order history</h2>
      {loading ? (
        <p className="text-muted-foreground py-10">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="border border-border py-16 text-center" data-testid="account-no-orders">
          <Package size={40} className="mx-auto text-muted-foreground/50" strokeWidth={1} />
          <p className="mt-4 text-muted-foreground">You have no orders yet.</p>
          <Link to="/shop" className="mt-5 inline-block bg-primary text-primary-foreground px-8 py-3 text-sm">
            Start shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="border border-border" data-testid={`account-order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-secondary/40 border-b border-border">
                <div>
                  <p className="font-display text-lg text-primary">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <span className={`text-xs px-3 py-1 font-semibold ${STATUS_COLORS[o.status] || "bg-muted"}`}>
                  {o.status}
                </span>
              </div>
              <div className="px-5 py-4 flex flex-wrap items-center gap-3">
                <div className="flex -space-x-3">
                  {o.items.slice(0, 4).map((i, idx) => (
                    <div key={idx} className="w-12 aspect-square bg-muted overflow-hidden border-2 border-background">
                      <img src={i.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <p className="text-sm text-foreground/80">{o.items.length} item{o.items.length > 1 ? "s" : ""}</p>
                  <p className="text-sm font-semibold text-primary">{inr(o.total)} · {o.payment_method === "cod" ? "COD" : "Paid"}</p>
                </div>
                <Link
                  to="/track"
                  state={{ order_number: o.order_number }}
                  className="inline-flex items-center gap-1 text-sm text-primary link-underline"
                >
                  Track <ChevronRight size={15} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
